/**
 * CostStrategiesTab Component
 * 
 * Displays care management strategies with CDM tiers and risk flags.
 * Implements Strategy Redesign Specification v1.1:
 * - CDM (Chronic Disease Management) with Tier 1/2
 * - PCM, SNF, ED, MedOpt, BH strategies
 * - Readmissions Watch as a risk flag (not a strategy)
 * - High-Cost filter (80% of cost cohort)
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft,
  Users,
  DollarSign,
  Target,
  ChevronRight,
  Eye,
  Check,
  Clock,
  Circle,
  Search,
  AlertTriangle,
  Activity,
  HeartPulse,
  Filter,
} from 'lucide-react';
import { PatientDetailModal } from './PatientDetailModal';

// ============================================
// Types
// ============================================

interface CDMTier {
  tier: number;
  name: string;
  description: string;
  candidateCount: number;
  highCostCandidateCount?: number;
  potentialSavings: number;
  highCostSavings?: number;
  reductionRate: number;
  reimbursement: string;
}

interface Strategy {
  id: string;
  name: string;
  shortName: string;
  description: string;
  impactStatement: string;
  candidateCount: number;
  highCostCandidateCount?: number;
  potentialSavings: number;
  highCostSavings?: number;
  reductionRate: number;
  // CDM-specific
  tiers?: CDMTier[];
  totalCandidates?: number;
  totalHighCostCandidates?: number;
  totalPotentialSavings?: number;
}

interface RiskFlag {
  id: string;
  name: string;
  description: string;
  patientCount: number;
  inCareManagement: number;
  notInCareManagement: number;
}

interface StrategyCandidate {
  mbi: string;
  gender: string;
  dob: string;
  dateOfDeath?: string | null;
  age: number;
  hccRisk: number;
  hccPercentile: number;
  totalCost: number;
  excessCost: number;
  measures: string[];
  hasReadmission: boolean;
  hasSNF: boolean;
  chronicConditionCount: number;
  cdmTier?: number;
  isHighCost?: boolean;
  costRank?: number;
  riskFlags?: string[];
  strategies: { id: string; name: string; shortName: string; reason: string }[];
}

interface HighCostAnalysis {
  patientCount: number;
  percentOfPopulation: number;
  costThreshold: number;
  avgCostInCohort: number;
  totalCostInCohort: number;
}

interface CostStrategiesTabProps {
  practiceId: string;
  npi?: string;
  initialStrategyId?: string | null;
  initialHighCostFilter?: boolean;
  cameFromSummary?: boolean;
  onStrategyIdConsumed?: () => void;
  onBackToSummary?: () => void;
}

// ============================================
// Strategy Definitions (per redesign spec v1.1)
// ============================================

const STRATEGY_DEFINITIONS: Omit<Strategy, 'candidateCount' | 'potentialSavings'>[] = [
  {
    id: 'cdm',
    name: 'Chronic Disease Management',
    shortName: 'CDM',
    description: 'Tiered care coordination combining CCM and RPM',
    impactStatement: '22-52% cost reduction',
    reductionRate: 0.35, // Blended average
  },
  {
    id: 'pcm',
    name: 'PCM Enrollment',
    shortName: 'PCM',
    description: 'Principal Care Management for high-cost single-condition patients',
    impactStatement: '$70/month billable, 20% reduction',
    reductionRate: 0.20,
  },
  {
    id: 'snf',
    name: 'SNF Optimization',
    shortName: 'SNF',
    description: 'Shift SNF patients to home health when clinically appropriate',
    impactStatement: '25% SNF cost reduction',
    reductionRate: 0.25,
  },
  {
    id: 'ed',
    name: 'ED Diversion',
    shortName: 'ED',
    description: 'Redirect non-emergent ED visits to appropriate care settings',
    impactStatement: '30-40% ED reduction',
    reductionRate: 0.40,
  },
  {
    id: 'medopt',
    name: 'Medication Optimization',
    shortName: 'MedOpt',
    description: 'Comprehensive medication review for diabetes/polypharmacy',
    impactStatement: '15-20% Part D savings',
    reductionRate: 0.175,
  },
  {
    id: 'bh',
    name: 'Behavioral Health Integration',
    shortName: 'BH',
    description: 'Integrated behavioral health services for depression/psychoses',
    impactStatement: '15% cost reduction',
    reductionRate: 0.15,
  },
];

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${Math.round(value / 1000)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  } catch {
    return '—';
  }
}

function getStatusBadge(status: string | null) {
  switch (status) {
    case 'in_progress':
      return (
        <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
          <Clock className="h-3 w-3 mr-1" />
          In Progress
        </Badge>
      );
    case 'complete':
      return (
        <Badge className="bg-green-100 text-green-800 border border-green-300">
          <Check className="h-3 w-3 mr-1" />
          Complete
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <Circle className="h-3 w-3 mr-1" />
          Not Started
        </Badge>
      );
  }
}

function getTierBadge(tier: number) {
  if (tier === 2) {
    return (
      <Badge className="bg-purple-100 text-purple-800 border border-purple-300 text-xs">
        <HeartPulse className="h-3 w-3 mr-1" />
        Tier 2: CCM + RPM
      </Badge>
    );
  }
  return (
    <Badge className="bg-blue-100 text-blue-800 border border-blue-300 text-xs">
      <Activity className="h-3 w-3 mr-1" />
      Tier 1: CCM
    </Badge>
  );
}

// ============================================
// Component
// ============================================

export function CostStrategiesTab({ practiceId, npi, initialStrategyId, initialHighCostFilter, cameFromSummary, onStrategyIdConsumed, onBackToSummary }: CostStrategiesTabProps) {
  // State
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [riskFlags, setRiskFlags] = useState<RiskFlag[]>([]);
  const [highCostAnalysis, setHighCostAnalysis] = useState<HighCostAnalysis | null>(null);
  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [candidates, setCandidates] = useState<StrategyCandidate[]>([]);
  const [patientStatuses, setPatientStatuses] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState(false);
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filters - use initial value if provided
  const [highCostFilterEnabled, setHighCostFilterEnabled] = useState(initialHighCostFilter || false);
  const [selectedTierFilter, setSelectedTierFilter] = useState<number | null>(null);
  
  // Modal
  const [selectedPatientMbi, setSelectedPatientMbi] = useState<string | null>(null);
  
  // Track if initial strategy has been consumed
  const [initialStrategyConsumed, setInitialStrategyConsumed] = useState(false);
  
  // Ref to track if data has been loaded at least once (prevents showing skeleton on tab switch)
  const hasLoadedRef = useRef(false);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  // Load strategies on mount
  useEffect(() => {
    loadStrategies();
  }, [practiceId, npi]);

  // Load candidates when strategy is selected
  useEffect(() => {
    if (selectedStrategy) {
      loadCandidates(selectedStrategy.id);
    }
  }, [selectedStrategy?.id, highCostFilterEnabled]);
  
  // Reset consumed flag when a new strategy ID is requested
  useEffect(() => {
    if (initialStrategyId) {
      setInitialStrategyConsumed(false);
    }
  }, [initialStrategyId]);
  
  // Sync highCostFilterEnabled with initialHighCostFilter prop when navigating from summary
  useEffect(() => {
    if (initialStrategyId && initialHighCostFilter !== undefined) {
      setHighCostFilterEnabled(initialHighCostFilter);
    }
  }, [initialStrategyId, initialHighCostFilter]);

  // Auto-select initial strategy when coming from summary page
  useEffect(() => {
    if (initialStrategyId && strategies.length > 0 && !initialStrategyConsumed) {
      const strategy = strategies.find(s => s.id === initialStrategyId);
      if (strategy) {
        setSelectedStrategy(strategy);
        setInitialStrategyConsumed(true);
        onStrategyIdConsumed?.();
      }
    }
  }, [initialStrategyId, strategies, initialStrategyConsumed, onStrategyIdConsumed]);

  const loadStrategies = async () => {
    // Only show loading skeleton on first load, not on subsequent refreshes
    if (!hasLoadedRef.current) {
      setIsLoading(true);
    }
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load strategies
      const strategiesUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/strategies`);
      if (npi) strategiesUrl.searchParams.set('npi', npi);
      
      const strategiesResponse = await fetch(strategiesUrl.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Load portfolio for high-cost analysis and risk flags
      const portfolioUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/portfolio`);
      if (npi) portfolioUrl.searchParams.set('npi', npi);
      
      const portfolioResponse = await fetch(portfolioUrl.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (strategiesResponse.ok) {
        const data = await strategiesResponse.json();
        
        // Merge API data with definitions (now includes highCostCandidateCount)
        const merged = STRATEGY_DEFINITIONS.map(def => {
          const apiStrategy = (data.strategies || []).find((s: any) => s.id === def.id);
          
          // Handle CDM specially - it has tiers
          if (def.id === 'cdm' && apiStrategy?.tiers) {
            return {
              ...def,
              tiers: apiStrategy.tiers,
              totalCandidates: apiStrategy.totalCandidates || apiStrategy.candidateCount || 0,
              totalHighCostCandidates: apiStrategy.totalHighCostCandidates || apiStrategy.highCostCandidateCount || 0,
              totalPotentialSavings: apiStrategy.totalPotentialSavings || apiStrategy.expectedSavings || 0,
              highCostSavings: apiStrategy.highCostSavings || 0,
              candidateCount: apiStrategy.totalCandidates || apiStrategy.candidateCount || 0,
              highCostCandidateCount: apiStrategy.totalHighCostCandidates || apiStrategy.highCostCandidateCount || 0,
              potentialSavings: apiStrategy.totalPotentialSavings || apiStrategy.expectedSavings || 0,
            };
          }
          
          return {
            ...def,
            candidateCount: apiStrategy?.candidateCount || 0,
            highCostCandidateCount: apiStrategy?.highCostCandidateCount || 0,
            potentialSavings: apiStrategy?.potentialSavings || apiStrategy?.expectedSavings || 0,
            highCostSavings: apiStrategy?.highCostSavings || 0,
          };
        }).sort((a, b) => b.potentialSavings - a.potentialSavings);
        
        setStrategies(merged);
        
        // Set risk flags from strategies endpoint
        if (data.riskFlags) {
          setRiskFlags(data.riskFlags);
        }
        
        // Set high cost analysis from strategies endpoint (primary source)
        if (data.highCostAnalysis) {
          setHighCostAnalysis(data.highCostAnalysis);
        }
      }
      
      if (portfolioResponse.ok) {
        const portfolioData = await portfolioResponse.json();
        
        // High cost analysis from portfolio (backup if strategies didn't provide it)
        if (portfolioData.highCostAnalysis && !highCostAnalysis) {
          setHighCostAnalysis(portfolioData.highCostAnalysis);
        }
        
        // Risk flags from portfolio (more detailed - overrides strategies)
        if (portfolioData.riskFlags?.readmitWatch) {
          setRiskFlags([{
            id: 'readmit_watch',
            name: 'Readmissions Watch',
            description: 'Patients with readmission history - priority for care management',
            patientCount: portfolioData.riskFlags.readmitWatch.patientCount || 0,
            inCareManagement: portfolioData.riskFlags.readmitWatch.inCareManagement || 0,
            notInCareManagement: portfolioData.riskFlags.readmitWatch.notInCareManagement || 0,
          }]);
        }
      }
    } catch (err) {
      console.error('Error loading strategies:', err);
    } finally {
      setIsLoading(false);
      hasLoadedRef.current = true;
    }
  };

  const loadCandidates = async (strategyId: string) => {
    setIsLoadingCandidates(true);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      // Load candidates
      const candidatesUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/strategies/${strategyId}/candidates`);
      if (npi) candidatesUrl.searchParams.set('npi', npi);
      candidatesUrl.searchParams.set('limit', '1000');
      if (highCostFilterEnabled) {
        candidatesUrl.searchParams.set('highCostOnly', 'true');
      }
      
      const candidatesResponse = await fetch(candidatesUrl.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (candidatesResponse.ok) {
        const data = await candidatesResponse.json();
        setCandidates(data.candidates || []);
      }
      
      // Load patient statuses
      const statusUrl = new URL(`${apiUrl}/api/practices/${practiceId}/cost/patient-status`);
      statusUrl.searchParams.set('strategyId', strategyId);
      
      const statusResponse = await fetch(statusUrl.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setPatientStatuses(statusData.statuses || {});
      }
    } catch (err) {
      console.error('Error loading candidates:', err);
    } finally {
      setIsLoadingCandidates(false);
    }
  };

  const updatePatientStatus = async (mbi: string, newStatus: string | null) => {
    if (!selectedStrategy) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      
      await fetch(`${apiUrl}/api/practices/${practiceId}/cost/patient-status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mbi,
          strategyId: selectedStrategy.id,
          status: newStatus,
        }),
      });
      
      // Update local state
      const key = `${selectedStrategy.id}-${mbi}`;
      setPatientStatuses(prev => ({
        ...prev,
        [key]: newStatus || 'not_started',
      }));
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const cycleStatus = (mbi: string) => {
    if (!selectedStrategy) return;
    
    const key = `${selectedStrategy.id}-${mbi}`;
    const currentStatus = patientStatuses[key];
    
    // Cycle: not_started → in_progress → complete → not_started
    let nextStatus: string | null;
    if (!currentStatus || currentStatus === 'not_started') {
      nextStatus = 'in_progress';
    } else if (currentStatus === 'in_progress') {
      nextStatus = 'complete';
    } else {
      nextStatus = null;
    }
    
    updatePatientStatus(mbi, nextStatus);
  };

  // Filter candidates
  const filteredCandidates = useMemo(() => {
    let result = candidates;
    
    // Search filter
    if (searchTerm) {
      result = result.filter(c =>
        c.mbi.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Tier filter (for CDM)
    if (selectedStrategy?.id === 'cdm' && selectedTierFilter) {
      result = result.filter(c => c.cdmTier === selectedTierFilter);
    }
    
    // High cost filter (client-side if API doesn't support it)
    if (highCostFilterEnabled) {
      result = result.filter(c => c.isHighCost);
    }
    
    return result;
  }, [candidates, searchTerm, selectedTierFilter, highCostFilterEnabled, selectedStrategy?.id]);
  
  const displayedCandidates = showAllCandidates 
    ? filteredCandidates 
    : filteredCandidates.slice(0, 20);

  // Get CDM strategy for tier summary
  const cdmStrategy = strategies.find(s => s.id === 'cdm');

  // Loading state - only show skeleton on first load, not on tab switches
  if (isLoading && !hasLoadedRef.current) {
    return (
      <div className="space-y-6 min-h-[500px]">
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  // Both views kept mounted, toggled with display:none/block for smooth transitions
  return (
    <>
      {/* Strategy Detail View - always mounted, toggled with display for instant switching */}
      <div style={{ display: selectedStrategy ? 'block' : 'none' }}>
        <div className="space-y-6 min-h-[500px]">
        {/* Header */}
        <div>
          <Button variant="ghost" size="sm" onClick={() => {
            if (cameFromSummary && onBackToSummary) {
              onBackToSummary();
            } else {
              setSelectedStrategy(null);
              setSelectedTierFilter(null);
            }
          }} className="-ml-2 mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            {cameFromSummary ? 'Back to Summary' : 'Back to Care Interventions'}
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold">{selectedStrategy?.name}</h2>
              <p className="text-muted-foreground mt-1">{selectedStrategy?.description}</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(selectedStrategy?.potentialSavings ?? 0)}
              </p>
              <p className="text-sm text-muted-foreground">potential savings</p>
            </div>
          </div>
        </div>

        {/* CDM Tier Summary (only for CDM) */}
        {selectedStrategy?.id === 'cdm' && selectedStrategy?.tiers && (
          <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg border">
            {selectedStrategy.tiers.map(tier => (
              <button
                key={tier.tier}
                onClick={() => setSelectedTierFilter(selectedTierFilter === tier.tier ? null : tier.tier)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  selectedTierFilter === tier.tier
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                    : 'bg-card hover:border-primary/50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  {getTierBadge(tier.tier)}
                  {selectedTierFilter === tier.tier && (
                    <Badge variant="secondary" className="text-xs">Filtered</Badge>
                  )}
                </div>
                <p className="font-medium">{tier.name}</p>
                <p className="text-xs text-muted-foreground mb-3">{tier.description}</p>
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">
                      {highCostFilterEnabled 
                        ? (tier.highCostCandidateCount ?? tier.candidateCount)
                        : tier.candidateCount}
                    </span>
                  </span>
                  <span className="flex items-center gap-1 text-green-600">
                    <DollarSign className="h-4 w-4" />
                    <span className="font-semibold">
                      {formatCurrency(highCostFilterEnabled 
                        ? (tier.highCostSavings ?? tier.potentialSavings)
                        : tier.potentialSavings)}
                    </span>
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {tier.reimbursement}
                </p>
              </button>
            ))}
          </div>
        )}

        {/* Filters and Controls */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by MBI..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox
              id="highCostFilter"
              checked={highCostFilterEnabled}
              onCheckedChange={(checked) => setHighCostFilterEnabled(checked === true)}
            />
            <label htmlFor="highCostFilter" className="text-sm cursor-pointer flex items-center gap-1">
              <Filter className="h-3 w-3" />
              High-Cost Only
              {highCostAnalysis && (
                <span className="text-muted-foreground">
                  ({highCostAnalysis.patientCount} patients)
                </span>
              )}
            </label>
          </div>
          
          <p className="text-sm text-muted-foreground ml-auto">
            {filteredCandidates.length} candidate patients
          </p>
        </div>

        {/* Candidates Table */}
        <div className="border rounded-lg overflow-hidden">
          {isLoadingCandidates ? (
            <div className="p-8 text-center">
              <Skeleton className="h-8 w-48 mx-auto" />
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium">MBI</th>
                  {selectedStrategy?.id === 'cdm' && (
                    <th className="text-center p-3 font-medium">Tier</th>
                  )}
                  <th className="text-center p-3 font-medium">Gender</th>
                  <th className="text-center p-3 font-medium">Age</th>
                  <th className="text-center p-3 font-medium">DOB</th>
                  <th className="text-center p-3 font-medium">Deceased</th>
                  <th className="text-right p-3 font-medium">HCC Risk</th>
                  <th className="text-right p-3 font-medium">Total Cost</th>
                  <th className="text-right p-3 font-medium">Opportunity</th>
                  <th className="text-center p-3 font-medium">Flags</th>
                  <th className="text-center p-3 font-medium">Status</th>
                  <th className="text-center p-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {displayedCandidates.length > 0 ? (
                  displayedCandidates.map((candidate, idx) => {
                    const statusKey = `${selectedStrategy?.id}-${candidate.mbi}`;
                    const status = patientStatuses[statusKey] || null;
                    
                    return (
                      <tr key={idx} className="border-t hover:bg-muted/30">
                        <td className="p-3 font-mono">
                          <div className="flex items-center gap-2">
                            {candidate.mbi}
                            {candidate.isHighCost && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs px-1">
                                HC
                              </Badge>
                            )}
                          </div>
                        </td>
                        {selectedStrategy?.id === 'cdm' && (
                          <td className="p-3 text-center">
                            {candidate.cdmTier && getTierBadge(candidate.cdmTier)}
                          </td>
                        )}
                        <td className="p-3 text-center">{candidate.gender}</td>
                        <td className="p-3 text-center">{candidate.age}</td>
                        <td className="p-3 text-center text-muted-foreground text-xs">
                          {formatDate(candidate.dob)}
                        </td>
                        <td className="p-3 text-center text-muted-foreground text-xs">
                          {formatDate(candidate.dateOfDeath)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {candidate.hccRisk?.toFixed(2) || '—'}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {formatCurrency(candidate.totalCost)}
                        </td>
                        <td className="p-3 text-right font-semibold text-amber-600">
                          {candidate.excessCost > 0 ? formatCurrency(candidate.excessCost) : '—'}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {candidate.hasReadmission && (
                              <Badge className="bg-red-100 text-red-700 text-xs px-1" title="Readmission">
                                R
                              </Badge>
                            )}
                            {candidate.hasSNF && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs px-1" title="SNF">
                                S
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <button
                            onClick={() => cycleStatus(candidate.mbi)}
                            className="hover:opacity-80 transition-opacity"
                          >
                            {getStatusBadge(status)}
                          </button>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedPatientMbi(candidate.mbi)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      No candidates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Show More */}
        {filteredCandidates.length > 20 && (
          <div className="text-center">
            <Button
              variant="outline"
              onClick={() => setShowAllCandidates(!showAllCandidates)}
            >
              {showAllCandidates
                ? 'Show Less'
                : `Show All (${filteredCandidates.length} patients)`}
            </Button>
          </div>
        )}

        </div>
      </div>

      {/* Strategy Cards View - kept mounted, toggled with display */}
      <div style={{ display: !selectedStrategy ? 'block' : 'none' }}>
        <div className="space-y-6 min-h-[500px]">
      {/* Header with Toggle (like Summary page) */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Care Interventions</h2>
          <p className="text-sm text-muted-foreground">
            Click an intervention to view candidate patients
          </p>
        </div>
        
        {/* Nice toggle like Summary page */}
        <div className="flex items-center gap-1 p-0.5 bg-slate-200 rounded-lg">
          <button
            onClick={() => setHighCostFilterEnabled(true)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              highCostFilterEnabled
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            High-Cost Patients
            {highCostAnalysis && (
              <span className="ml-1 text-xs text-muted-foreground">
                ({highCostAnalysis.patientCount})
              </span>
            )}
          </button>
          <button
            onClick={() => setHighCostFilterEnabled(false)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              !highCostFilterEnabled
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            All Candidates
          </button>
        </div>
      </div>

      {/* Strategy Cards */}
      <div className="grid grid-cols-3 gap-4">
        {strategies.map(strategy => {
          const isCDM = strategy.id === 'cdm';
          
          return (
            <div
              key={strategy.id}
              onClick={() => setSelectedStrategy(strategy)}
              className="bg-card border rounded-lg p-4 cursor-pointer hover:border-primary hover:shadow-md transition-all group"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold group-hover:text-primary transition-colors">
                    {strategy.name}
                  </h3>
                  {isCDM && (
                    <Badge variant="outline" className="text-xs">
                      Tiered
                    </Badge>
                  )}
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
              </div>

              {/* Description */}
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {strategy.description}
              </p>

              {/* CDM Tier Preview - simplified for card view */}
              {isCDM && strategy.tiers && (
                <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
                  <span>Tier 1: {highCostFilterEnabled 
                    ? (strategy.tiers[0]?.highCostCandidateCount ?? strategy.tiers[0]?.candidateCount ?? 0)
                    : (strategy.tiers[0]?.candidateCount || 0)} patients</span>
                  <span>Tier 2: {highCostFilterEnabled 
                    ? (strategy.tiers[1]?.highCostCandidateCount ?? strategy.tiers[1]?.candidateCount ?? 0)
                    : (strategy.tiers[1]?.candidateCount || 0)} patients</span>
                </div>
              )}

              {/* Standard Metrics - Dynamic based on toggle */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-muted/50 rounded-lg p-2 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-lg font-bold">
                      {highCostFilterEnabled 
                        ? (isCDM ? (strategy.totalHighCostCandidates ?? strategy.totalCandidates ?? 0) : (strategy.highCostCandidateCount ?? strategy.candidateCount))
                        : (isCDM ? strategy.totalCandidates : strategy.candidateCount)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">patients</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <span className="text-lg font-bold text-green-700">
                    {formatCurrency(highCostFilterEnabled
                      ? (isCDM ? (strategy.highCostSavings || strategy.totalPotentialSavings || 0) : (strategy.highCostSavings || strategy.potentialSavings))
                      : (isCDM ? (strategy.totalPotentialSavings || 0) : strategy.potentialSavings))}
                  </span>
                  <p className="text-xs text-green-600">potential</p>
                </div>
              </div>

              {/* Impact Statement */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Target className="h-3 w-3" />
                <span>Impact: {strategy.impactStatement}</span>
              </div>
            </div>
          );
        })}

        {/* Readmissions Watch Card - styled same as other strategies */}
        {riskFlags.length > 0 && (
          <div
            onClick={() => {
              // Navigate to readmissions watch detail
              const readmitFlag = riskFlags[0];
              setSelectedStrategy({
                id: 'readmit_watch',
                name: 'Readmissions Watch',
                shortName: 'Readmit',
                description: 'Patients with readmission history requiring priority attention',
                type: 'risk_flag',
                candidateCount: readmitFlag.patientCount,
                highCostCandidateCount: readmitFlag.highCostPatientCount || 0,
                potentialSavings: 0,
                highCostSavings: 0,
                impactStatement: `${readmitFlag.notInCareManagement} need enrollment`,
                tiers: undefined
              });
            }}
            className="bg-card border rounded-lg p-4 cursor-pointer hover:border-primary hover:shadow-md transition-all group"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold group-hover:text-primary transition-colors">
                  Readmissions Watch
                </h3>
                <Badge variant="outline" className="text-xs border-amber-300 text-amber-700">
                  Risk Flag
                </Badge>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>

            {/* Description */}
            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
              Patients with readmission history - prioritize for care management enrollment
            </p>

            {/* Metrics - same style as other strategies */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="bg-muted/50 rounded-lg p-2 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-lg font-bold">
                    {highCostFilterEnabled 
                      ? (riskFlags[0].highCostPatientCount ?? riskFlags[0].patientCount)
                      : riskFlags[0].patientCount}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">patients</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-2 text-center">
                <span className="text-lg font-bold text-amber-700">{riskFlags[0].notInCareManagement}</span>
                <p className="text-xs text-amber-600">need enrollment</p>
              </div>
            </div>

            {/* Impact Statement - same style as other strategies */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Target className="h-3 w-3" />
              <span>Impact: {riskFlags[0].inCareManagement} already in care management</span>
            </div>
          </div>
        )}
        </div>
        </div>
      </div>

      {/* Patient Detail Modal - outside both views so it's always available */}
      <PatientDetailModal
        isOpen={!!selectedPatientMbi}
        onClose={() => setSelectedPatientMbi(null)}
        patientMbi={selectedPatientMbi}
        practiceId={practiceId}
        npi={npi}
        onStrategyRemoved={() => {
          if (selectedStrategy) {
            loadCandidates(selectedStrategy.id);
          }
        }}
      />
    </>
  );
}

export default CostStrategiesTab;
